import {
    app,
    payload,
    EventData,
    Router,
    each as ojsEach,
} from "modular-openscriptjs";
import { Preferences } from "@capacitor/preferences";
import { Geolocation } from "@capacitor/geolocation";
import { Capacitor } from "@capacitor/core";
import { dom, Modal } from "./fotastart";
import { analyze } from "./analytics";
import {
    _UNIQUE_ID_,
    ACTION_CALLBACKS,
    MainActionData,
    NavItem,
    NoticeWrapper,
    gc,
    uc,
    dc,
    pc,
    STRINGS,
} from "./declarations";

export async function saveUser(user) {
    if (user && Object.keys(user).length > 0) {
        await Preferences.set({ key: "user", value: JSON.stringify(user) });
    } else {
        await Preferences.remove({ key: "user" });
    }
}

export function either(condition, statement1 = null, statement2 = null) {
    const value = (s) => {
        return typeof s === "function" ? s() : s;
    };

    return condition ? value(statement1) : value(statement2);
}

export function oneOf(statement1 = null, statement2 = null) {
    const value = (s) => {
        return typeof s === "function" ? s() : s;
    };

    return value(statement1) ?? value(statement2);
}

/**
 *
 * @param {object|Array} object
 * @param {(object, key) => any} callback
 * @returns {Array}
 */
export function each(object, callback) {
    return ojsEach(object, callback);
}

/**
 * Returns an object's property value if it exist or the default value
 * @param {object} object object to search
 * @param {string} property property to look for in object
 * @param {*} def default
 */
export function notIn(object, property, def = null) {
    if (property in object) return object[property];
    return def;
}

/**
 * The monetary export function formats a given value as a currency using the specified currency code.
 * @param {number} value - The value parameter represents the numerical value that you want to format as a
 * monetary value. It can be any number, positive or negative.
 * @param {string} [currency=KES] - The currency parameter is a string that represents the currency code. In the
 * provided code, the default currency is set to 'KES', which represents Kenyan Shilling.
 * @returns {string} a formatted string representation of the value with the specified currency symbol.
 */
export function monetary(value, currency = "KES") {
    return Intl.NumberFormat("en-Us", {
        style: "currency",
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
}

export function inform(
    message,
    label = STRINGS.INFO,
    timeout = 2000,
    autohide = true,
    position = "top-end",
) {
    new NoticeWrapper(message, label, autohide, timeout, position);
}

// jesse did this
export function eParse(ed) {
    return EventData.parse(ed);
}

export function mergeDetails(details = []) {
    let final = {};

    for (let detail of details) {
        final[detail.field_name] = detail.field_value;
    }

    return final;
}

export function formatDate(dateString) {
    return `${new Date(dateString).toLocaleDateString()} ${new Date(
        dateString,
    ).toLocaleTimeString()}`;
}

export function authenticated() {
    return gc.auth.value ?? false;
}

export function unauthenticated() {
    return !authenticated();
}

export function validPhone() {
    return /^\+?\d{9,12}$/g.test(gc?.user?.value?.phone);
}

export function afterAuth(callback) {
    gc.afterAuth = gc.afterAuth ?? [];
    gc.afterAuth.push(callback);
}

export function runAfterAuth() {
    if (!gc.afterAuth) return;

    try {
        return gc.afterAuth.forEach((f) => f());
    } catch (e) {
        console.error(e);
    }
}

export function insertAfterIndex(arr, value, index) {
    const result = [...arr];

    if (index < -1) {
        result.unshift(value);
    } else if (index >= arr.length - 1) {
        result.push(value);
    } else {
        result.splice(index + 1, 0, value);
    }

    return result;
}

export function approved(event, eventPayload, text = null) {
    const { meta, message } = eParse(eventPayload);
    const broker = app("broker");
    const $e = app("$e");

    if (text !== null) meta.put("__ct", text);

    if (meta.get("__event") !== event) {
        meta.put("__event", event);
        broker.send($e.system.needs.approval, payload(message, meta));

        return false;
    }

    if (!meta.has("__confirmed")) {
        meta.put("__event", event);
        broker.send($e.system.needs.approval, payload(message, meta));
        return false;
    }

    if (!meta.get("__confirmed", false)) {
        return false;
    }

    return true;
}

export function authorizedByPin(event, eventPayload, text = null) {
    const { meta, message } = eParse(eventPayload);
    const broker = app("broker");
    const $e = app("$e");

    if (text !== null) meta.put("__ct", text);

    if (meta.get("__event") !== event) {
        meta.put("__event", event);
        broker.send($e.system.needs.pin, payload(message, meta));

        return false;
    }

    if (!meta.has("__confirmed")) {
        meta.put("__event", event);
        broker.send($e.system.needs.pin, payload(message, meta));
        return false;
    }

    if (!meta.get("__confirmed", false)) {
        return false;
    }

    return true;
}

/**
 * Broadcasts an event from an HTML visible markup with no event payload.
 * @param {string|Array<event>} event
 */
export function sendEvent(event, _payload = null) {
    const broker = app("broker");
    broker.send(event, _payload ? _payload : payload());
}

/**
 * Broadcasts an event from an HTML visible markup with no event payload.
 * @param {string|Array<string>} event
 */
export function fireEvent(event, payload = null) {
    sendEvent(event, payload);
}

export function acknowledge(
    config = {
        title: "Confirmed",
        message: "Woohoo!! The operation was successful",
        type: "success",
        close_text: "OK",
        action: {
            route_name: null,
            route_params: null,
        },
    },
) {
    const broker = app("broker");
    const $e = app("$e");

    const finalConfig = {
        title: config.title ?? "Confirmation",
        message: config.message ?? "",
        type: config.type ?? "success",
        close_text: config.close_text ?? "OK",
        action: {
            route_name: config.action?.route_name ?? null,
            route_params: config.action?.route_params ?? {},
        },
    };

    broker.send($e.system.has.confirmation, payload(finalConfig));
}

export function processStart(data = {}) {
    const broker = app("broker");
    broker.send(app("$e").app.indicator.process.start, payload(data));
}

export function processEnd() {
    const broker = app("broker");
    broker.send(app("$e").app.indicator.process.end);
}

export function pageInfo(pageName) {
    pc.pageTitle.value = pageName;
    NavItem.page(pageName);
    dom.get("title").innerHTML = pageName;
}

export function isFirstLoad() {
    if (performance.navigation.type === performance.navigation.TYPE_RELOAD) {
        return true;
    } else {
        return false;
    }
}

/**
 * Checks if the user has a permission
 * @param {string} permission
 * @returns
 */
export function can(permission) {
    return uc.permissions.value[permission] == true;
}

/**
 * Checks if the user has a role
 * @param {string} role
 * @returns
 */
export function is(role) {
    return uc.roles.value[role] == true;
}

export function uniqueId() {
    return _UNIQUE_ID_.getId();
}

export function randomColor() {
    let color = Math.floor(Math.random() * 16777215).toString(16);

    for (let i = color.length; i < 6; i++) {
        color += "0";
    }

    return color;
}

export function validatePhoneNumber(phoneNumber) {
    const phoneRegex = /^\+?\d{9,12}$/g;
    return phoneRegex.test(phoneNumber);
}

export function normalizePhoneNumber(phoneNumber) {
    if (phoneNumber.startsWith("+2540")) {
        return "+254" + phoneNumber.slice(5);
    }
    return phoneNumber;
}

export function getStatusClass(status) {
    switch (status) {
        case "refund_pending":
        case "pending":
            return { class: "bg-light text-dark" };
        case "confirmed":
            return { class: "bg-secondary" };
        case "refunded":
        case "dispute":
            return { class: "bg-warning" };
        case "processing":
            return { class: "bg-info" };
        case "delivering":
            return { class: "bg-primary" };
        case "completed":
        case "paid":
            return { class: "bg-success" };
        case "failed":
        case "cancelled":
            return { class: "bg-danger" };
        default:
            return {};
    }
}

export function getAllAdditionalPhoneNumbers(data) {
    if (Array.isArray(data.message.additional_phone_number)) {
        return data.message.additional_phone_number.map(
            (entry) => entry.additional_phone_number,
        );
    }
    return [];
}

export function removeCountryCode(phoneNumber) {
    phoneNumber = phoneNumber.replace(/^\+/, "");
    const firstDigitIndex = phoneNumber.match(/^[0-9]{1,3}/)[0].length;
    return phoneNumber.substring(firstDigitIndex);
}

export function getValuesOfAdditionalNumberChildren(parentId) {
    let parentElement = document.getElementById(parentId);
    if (!parentElement) {
        console.error(`Element with ID "${parentId}" not found.`);
        return [];
    }

    let formFields = parentElement.querySelectorAll(".form-field");
    let values = [];

    formFields.forEach((field) => {
        let selectElement = field.querySelector("select");
        let inputElement = field.querySelector('input[type="tel"]');

        let fieldValue = {
            // country_code: selectElement ? selectElement.value : null,
            // phone_number: inputElement ? inputElement.value : null,
            additional_phone_number: normalizePhoneNumber(
                `${selectElement.value}${inputElement.value}`,
            ),
        };

        values.push(fieldValue);
    });

    return values;
}

export function getValue(object, key, def = null) {
    if (empty(object)) return def;
    const v = object[key] ?? def;

    if (empty(v)) return def;

    return v;
}

export function formatDecimal(decimalNumber) {
    return Math.trunc(decimalNumber * 10) / 10;
}

export function formatThousand(num) {
    if (num >= 1e12) {
        // Convert to trillion
        return (num / 1e12).toFixed(num % 1e12 !== 0 ? 1 : 0) + "T";
    } else if (num >= 1e9) {
        // Convert to billion
        return (num / 1e9).toFixed(num % 1e9 !== 0 ? 1 : 0) + "B";
    } else if (num >= 1e6) {
        // Convert to million
        return (num / 1e6).toFixed(num % 1e6 !== 0 ? 1 : 0) + "M";
    } else if (num >= 1000) {
        // Convert to thousands (k)
        return (num / 1000).toFixed(num % 1000 !== 0 ? 1 : 0) + "k";
    } else {
        return num.toString();
    }
}

export function getDayOfTheWeek() {
    const dayOfWeek = new Date().toLocaleString("default", {
        weekday: "long",
    });

    return dayOfWeek;
}

export function getGreetings() {
    var currentHour = new Date().getHours();

    if (currentHour >= 5 && currentHour < 12) {
        return "Good morning";
    } else if (currentHour >= 12 && currentHour < 18) {
        return "Good afternoon";
    } else {
        return "Good evening";
    }
}

export function isCurrentTimeInRange(startTime, endTime) {
    const now = new Date();
    const currentTime = now.toTimeString().split(" ")[0];

    function timeToSeconds(time) {
        const [hours, minutes, seconds] = time.split(":").map(Number);
        return hours * 3600 + minutes * 60 + seconds;
    }

    const startSeconds = timeToSeconds(startTime);
    const endSeconds = timeToSeconds(endTime);
    const currentSeconds = timeToSeconds(currentTime);

    return currentSeconds >= startSeconds && currentSeconds <= endSeconds;
}

export function appIsLocal() {
    return /^(127\.0\.0\.1|localhost)$/.test(app("router").url().hostname);
}

export function isQualifiedUrl(url) {
    try {
        new URL(url);
        return true;
    } catch (e) {
        return false;
    }
}

export function IsDayForDeliveries() {
    if (appIsLocal()) return true;

    let currentDay = getDayOfTheWeek();
    if (!(currentDay in dc.deliveryDays)) return false;

    return isCurrentTimeInRange(
        dc.deliveryDays[currentDay].opens_at,
        dc.deliveryDays[currentDay].closes_at,
    );
}

export function openCustomerSupport() {
    analyze("click_customer_support");
    location.href = "https://tawk.to/chat/656b2fe8bfb79148e59969d4/1hglb6993";
}

class Cookie {
    /**
     *
     * @param {string} name
     * @param {string} value
     * @param {int} expireAfter days
     */
    static save(name, value, expireAfter = 365) {
        const d = new Date();
        d.setTime(d.getTime() + expireAfter * 24 * 60 * 60 * 1000);
        let expires = "expires=" + d.toUTCString();
        document.cookie = name + "=" + value + ";" + expires + ";path=/";
    }

    static get(name) {
        name = name + "=";
        let ca = document.cookie.split(";");
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) == " ") {
                c = c.substring(1);
            }
            if (c.indexOf(name) == 0) {
                return c.substring(name.length, c.length);
            }
        }

        return null;
    }

    static has(name, empty = false) {
        let value = Cookie.get(name);

        if (value == null || (value?.length == 0 && !empty)) return false;

        return true;
    }
}

export function delay(callback, seconds) {
    setTimeout(callback, seconds * 1000);
}

export function tab(nav, pane) {
    return {
        tab: nav,
        content: pane,
    };
}

export function safeJSONParse(jsonString) {
    try {
        // Step 1: Unescape JSON strings to handle double-escaped characters
        const unescapedJSON = jsonString.replace(/\\./g, (match) => {
            switch (match) {
                case '\\"':
                    return '"';
                case "\\n":
                    return "\n";
                case "\\t":
                    return "\t";
                // Add more escape sequences as needed
                default:
                    return match[1]; // Remove the backslash
            }
        });

        // Step 2: Parse the unescaped JSON
        const parsedData = JSON.parse(unescapedJSON);

        return parsedData;
    } catch (error) {
        console.error("Error parsing JSON:", error);
        return null; // Handle the error gracefully or throw an exception if necessary
    }
}

/**
 *
 * @param {HTMLElement} element
 * @param {MouseEvent} event
 * @returns
 */
export function wasClicked(element, event) {
    return element == event.target || element?.contains(event.target);
}

export function hideCanvas(selector) {
    if (!dom.get(`${selector}.show`)) return;
    let canvas = bootstrap.Offcanvas.getInstance(dom.get(selector));
    canvas?.hide();
}

export function hideModal(selector) {
    if (!dom.get(`${selector}.show`)) return;
    let modal = bootstrap.Modal.getInstance(dom.get(selector));
    modal?.hide();
}

export function cleanDispose(bootstrapElem) {
    bootstrapElem?._element?.remove();
    bootstrapElem?.dispose();
}

export function disposeBootstrapTree(root) {
    if (!root || !(root instanceof Element)) return;

    /* ============================================================
     * 1. Tooltips & Popovers (FIRST)
     *    - Attach global listeners
     *    - Often leak via closures
     * ============================================================ */
    root.querySelectorAll(
        '[data-bs-toggle="tooltip"], [data-bs-toggle="popover"]',
    ).forEach((el) => {
        bootstrap.Tooltip.getInstance(el)?.dispose();
        bootstrap.Popover.getInstance(el)?.dispose();
    });

    /* ============================================================
     * 2. Dropdowns
     *    - Document-level click listeners
     * ============================================================ */
    root.querySelectorAll(".dropdown-toggle").forEach((el) => {
        bootstrap.Dropdown.getInstance(el)?.dispose();
    });

    /* ============================================================
     * 3. Collapse / Accordion
     *    - May reference targets outside root
     * ============================================================ */
    root.querySelectorAll('[data-bs-toggle="collapse"]').forEach((el) => {
        bootstrap.Collapse.getInstance(el)?.dispose();
    });

    /* ============================================================
     * 4. Modals (SPECIAL CASE)
     *    - Backdrop
     *    - Body class
     *    - Focus trap
     * ============================================================ */
    root.querySelectorAll(".modal").forEach((el) => {
        const modal = bootstrap.Modal.getInstance(el);
        if (!modal) return;

        // Ensure proper shutdown order
        modal.hide();
        modal.dispose();
    });

    /* ============================================================
     * 5. Offcanvas
     *    - Similar to modal but less aggressive
     * ============================================================ */
    root.querySelectorAll(".offcanvas").forEach((el) => {
        const offcanvas = bootstrap.Offcanvas.getInstance(el);
        if (!offcanvas) return;

        offcanvas.hide();
        offcanvas.dispose();
    });

    /* ============================================================
     * 6. ScrollSpy
     *    - Observers + scroll listeners
     * ============================================================ */
    root.querySelectorAll('[data-bs-spy="scroll"]').forEach((el) => {
        bootstrap.ScrollSpy.getInstance(el)?.dispose();
    });

    /* ============================================================
     * 7. Carousels
     *    - Timers + RAF loops
     * ============================================================ */
    root.querySelectorAll(".carousel").forEach((el) => {
        bootstrap.Carousel.getInstance(el)?.dispose();
    });

    /* ============================================================
     * 8. Toasts
     * ============================================================ */
    root.querySelectorAll(".toast").forEach((el) => {
        bootstrap.Toast.getInstance(el)?.dispose();
    });

    /* ============================================================
     * 9. Remove leftover backdrops (defensive)
     * ============================================================ */
    document
        .querySelectorAll(".modal-backdrop, .offcanvas-backdrop")
        .forEach((el) => el.remove());
}

export function isMobile() {
    return window.screen.availWidth <= 576;
}

/**
 * True on iPhone/iPad/iPod Safari (and other iOS browsers, which all embed
 * WebKit) - the platform where "Add to Home Screen" is the only way to get
 * an app-like install, since there's no real App Store listing.
 */
export function isIOS() {
    return /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * True when the app is already running as an installed/standalone PWA
 * (launched from a home-screen icon rather than a Safari/Chrome tab).
 */
export function isStandalone() {
    return (
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
    );
}

export function scrollToTop() {
    fireEvent(app("$e").app.needs.to.scrollToTop);
}

/**
 * @param month
 */
export function getMonthName(month) {
    const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];

    return monthNames[month];
}

export function randomInt(min, max) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function empty(variable) {
    if (!variable) return true;
    if (Array.isArray(variable) && variable.length == 0) return true;
    if (typeof variable == "object" && Object.keys(variable).length == 0)
        return true;
    if (typeof variable == "undefined") return true;
    if (typeof variable == "string" && variable.length == 0) return true;

    return false;
}

export function insertSpaces(input) {
    return input.replace(/([A-Z])/g, " $1").trim();
}

export async function setCurrentLocationCoordinates(
    withDefault = true,
    callback = null,
    silent = false,
) {
    if (withDefault) {
        dc.latitude = -1.286389;
        dc.longitude = 36.817223;
    }

    let proceed = false;

    try {
        if (!Capacitor.isNativePlatform()) {
            if (!navigator.geolocation) {
                inform(
                    "Geolocation is not supported by your browser",
                    STRINGS.WARNING,
                );
                return;
            }

            if (!silent) {
                processStart({ title: "Getting your current address" });
            }

            const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(
                    resolve,
                    (err) => {
                        if (err.code === err.PERMISSION_DENIED) {
                            reject(new Error("User denied Geolocation"));
                        } else {
                            reject(err);
                        }
                    },
                    {
                        enableHighAccuracy: true,
                        timeout: 20000,
                        maximumAge: 10000,
                    },
                );
            });

            dc.latitude = position.coords.latitude;
            dc.longitude = position.coords.longitude;
            if (!silent) {
                processEnd();
            }

            if (callback) {
                callback(position.coords.latitude, position.coords.longitude);
            }
            return;
        }

        const permStatus = await Geolocation.checkPermissions();

        if (permStatus.location === "granted") {
            proceed = true;
        } else if (permStatus.location === "prompt") {
            const result = await Swal.fire({
                title: "Use Current Location?",
                text: "To get your current address, you need to turn on your location",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Yes",
                cancelButtonText: "No",
                reverseButtons: true,
            });
            if (result.isConfirmed) {
                const requestResult = await Geolocation.requestPermissions();
                proceed = requestResult.location === "granted";
            }
        } else {
            inform(
                "We can't get your current location. Please reset Carata's permission.",
                STRINGS.INFO,
            );
        }

        if (!proceed) return;

        if (!silent) {
            processStart({ title: "Getting your current address" });
        }

        const position = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 20000,
            maximumAge: 10000,
        });

        dc.latitude = position.coords.latitude;
        dc.longitude = position.coords.longitude;
        if (!silent) {
            processEnd();
        }

        if (callback) {
            callback(position.coords.latitude, position.coords.longitude);
        }
    } catch (error) {
        processEnd();
        console.error(error);

        switch (error.message) {
            case "User denied Geolocation":
                inform(
                    "You denied Carata access to your location, please grant access",
                    STRINGS.ERROR,
                );
                break;
            default:
                inform("Could not get your current location", STRINGS.WARNING);
        }
    }
}

export function arrayFlip(obj) {
    let flipped = {};

    for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
            flipped[obj[key]] = key;
        }
    }
    return flipped;
}

export function lastOf(arr) {
    if (arr.length === 0) {
        return null;
    }

    return arr[arr.length - 1];
}

export function deepEqual(a, b) {
    if (a === b) return true;

    if (
        typeof a !== "object" ||
        typeof b !== "object" ||
        a == null ||
        b == null
    ) {
        return false;
    }

    if (Array.isArray(a) !== Array.isArray(b)) return false;

    if (Array.isArray(a)) {
        if (a.length !== b.length) return false;
        return a.every((item, i) => deepEqual(item, b[i]));
    }

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    return keysA.every((key) => deepEqual(a[key], b[key]));
}

export function arraysEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((item, i) => deepEqual(item, arr2[i]));
}

/**
 *
 * @param {string} path - or route name
 * @returns {Router} router
 */
export function redirect(path = null) {
    if (empty(path)) return app("router");

    return app("router").to(path);
}

export function range(start, end, increment = 1) {
    const output = [];
    for (let i = start; i <= end; i += increment) output.push(i);
    return output;
}

export function minimizeString(str, length) {
    if (str.length <= length) return str;

    if (length <= 3) return str.slice(0, length);

    const side = Math.floor((length - 3) / 2);
    const start = str.slice(0, side);
    const end = str.slice(str.length - (length - side - 3));

    return start + "..." + end;
}

export function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024; // binary (use 1000 for decimal SI units)
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return (
        parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + " " + sizes[i]
    );
}

/**
 *
 * @param {string} name button Label
 * @param {export function} callback callback(MainActionForm)
 * @param {boolean} closeable
 */
export function showActionButton(
    callback,
    name = "Continue",
    closeable = false,
) {
    const callbackId = `cb_${_UNIQUE_ID_}`;
    ACTION_CALLBACKS[callbackId] = (mainActionForm, callbackId) =>
        callback(mainActionForm, callbackId);
    let ma = MainActionData.from({ name, callbackId });
    ma.closeable = closeable;
    const broker = app("broker");
    const $e = app("$e");
    broker.send($e.app.needs.mainActionPrompt, payload({ data: ma }));

    return callbackId;
}

export function formatDateForHumans(dateInput) {
    const date = new Date(dateInput);
    const now = new Date();

    const options = { hour: "numeric", minute: "numeric", hour12: true };
    const timeString = date.toLocaleTimeString("en-US", options);

    const oneDay = 86400000; // 1 day in milliseconds
    const startOfToday = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
    );
    const startOfTomorrow = new Date(startOfToday.getTime() + oneDay);
    const startOfYesterday = new Date(startOfToday.getTime() - oneDay);

    if (date >= startOfToday && date < startOfTomorrow) {
        return `Today ${timeString}`;
    } else if (date >= startOfYesterday && date < startOfToday) {
        return `Yesterday ${timeString}`;
    } else if (
        date >= startOfTomorrow &&
        date < startOfTomorrow.getTime() + oneDay
    ) {
        return `Tomorrow ${timeString}`;
    }

    const startOfWeek = new Date(
        startOfToday.getTime() - startOfToday.getDay() * oneDay,
    );
    if (
        date >= startOfWeek &&
        date < startOfToday.getTime() + (7 - startOfToday.getDay()) * oneDay
    ) {
        return `${date.toLocaleDateString("en-US", {
            weekday: "long",
        })} ${timeString}`;
    }

    return (
        date.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric",
        }) + ` ${timeString}`
    );
}

export function initials(str) {
    const parts = str.trim().split(/\s+/);
    const first = parts[0][0];
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase();
}

export function formatDuration(seconds) {
    const days = Math.floor(seconds / 86400);
    seconds %= 86400;

    const hours = Math.floor(seconds / 3600);
    seconds %= 3600;

    const minutes = Math.floor(seconds / 60);

    const parts = [];

    if (days > 0) parts.push(`${days} D`);
    if (hours > 0) parts.push(`${hours} hr${hours > 1 ? "s" : ""}`);
    if (minutes > 0) parts.push(`${minutes} min${minutes > 1 ? "s" : ""}`);

    // Handle case when duration < 1 minute
    if (parts.length === 0) return "less than 1 min";

    return parts.join(", ");
}

export function findByProperty(arr, prop, value) {
    return arr.find((obj) => obj[prop] === value) || null;
}

export async function store(key, value) {
    if (typeof value === "object") value = JSON.stringify(value);

    localStorage.setItem(key, value);
    await Preferences.set({ key, value: String(value) });
}

export async function getStore(key) {
    let { value } = await Preferences.get({ key });

    if (!value) value = localStorage.getItem(key);

    return value ? JSON.parse(value) : null;
}

export function getStoreSync(key) {
    return localStorage.getItem(key);
}

export async function deleteFromStorage(key) {
    localStorage.removeItem(key);
    await Preferences.remove({ key });
}

/**
 * Builds the storage key used to persist the OTP verification timestamp.
 * Scoped per user id so that one account's OTP verification can never be
 * reused by a different account signed in on the same device.
 * @param {string|number} userId
 */
export function otpVerifiedAtKey(userId) {
    return `otp_verified_at_${userId}`;
}

/**
 * Loads the OTP verification timestamp for the given user id and updates
 * `window.otpVerifiedAt` accordingly (0 if the user has none, or if no
 * user id is provided). Call this whenever the signed-in user changes —
 * on boot, after login/registration, and after any session refresh —
 * so a previous account's OTP grace period is never applied to another.
 * @param {string|number|undefined|null} userId
 */
export async function refreshOtpVerifiedAt(userId) {
    if (!userId) {
        window.otpVerifiedAt = 0;
        return;
    }

    const { value } = await Preferences.get({ key: otpVerifiedAtKey(userId) });
    window.otpVerifiedAt = value ? parseInt(value) : 0;
}

// equivalent to export

/**
 * Check corresponding to CSS selector `input:placeholder-shown`
 * @param {HTMLInputElement} input
 */
export function hasPlaceholderShown(input) {
    if (!input) return false;
    const hasPlaceholder = input.hasAttribute("placeholder");
    // Editable <div> case
    if (input.getAttribute("contenteditable") == "true") {
        return hasPlaceholder && input.innerText.trim() == "";
    }
    return hasPlaceholder && input.value == "";
}

/**
 * Checks if the given value is an object.
 * @param {*} value - The value to check.
 * @returns {boolean} Returns `true` if the given value is an object, else `false`.
 */
export function isObject(value) {
    return typeof value === "object" && value !== null;
}

/**
 * Short reference to Object.prototype.hasOwnProperty.
 */
export var { hasOwnProperty } = Object.prototype;

/**
 * Checks if the given value is a plain object.
 * @param {*} value - The value to check.
 * @returns {boolean} Returns `true` if the given value is a plain object, else `false`.
 */
export function isPlainObject(value) {
    // This solution aligns with lodash, jQuery, redux implementations
    if (!isObject(value)) return false;

    // Accept objects with null prototype
    // Object.create(null)
    const proto = Object.getPrototypeOf(value);
    if (proto === null) return true;

    try {
        const { constructor } = value;
        const { prototype } = constructor;
        return (
            constructor &&
            prototype &&
            hasOwnProperty.call(prototype, "isPrototypeOf")
        );
    } catch {
        return false;
    }
}

/**
 * Extracts all direct key-value pairs from an object
 * where the value is not a plain object
 */
export function extractNonObjectPairs(obj) {
    const result = {};
    try {
        // If o is null or undefined, Object.entries(o) throws a TypeError
        for (const [key, value] of Object.entries(obj)) {
            if (!isPlainObject(value)) {
                result[key] = value;
            }
        }
    } catch (error) {} // pass
    return result;
}

/**
 * Trigger a DOM reflow. Helps restart an element's animation.
 * @param {HTMLElement} element
 * @return void
 * @see https://www.harrytheo.com/blog/2021/02/restart-a-css-animation-with-javascript/#restarting-a-css-animation
 */
export function reflow(element) {
    void element.offsetWidth;
}

/**
 * Convert a CSS time value to a valid Number (milliseconds)
 * @param {String} duration CSS time value e.g., 250ms or 0.5s
 * @returns {Number}
 */
export function parseCSSDuration(duration) {
    if (duration.endsWith("ms")) {
        return parseFloat(duration);
    }
    if (duration.endsWith("s")) {
        return parseFloat(duration) * 1000;
    }
    return 0;
}

/**
 * Open static modal (not closed by backdrop click or Escape key)
 * @param {String} id - The ID of the modal to open.
 * @param {Object} options - Modal options.
 */
export function openModal(id, options) {
    Modal.open(id, options);
}

/**
 * Open closeable modal (closed by backdrop click or Escape key)
 * @param {String} id - The ID of the modal to open.
 * @param {Object} options - Modal options.
 */
export function openCloseableModal(id, options) {
    Modal.closeable(id, options);
}

/**
 * Close open modal and/or cleanup saved data
 * @param {String} id - The ID of the modal to close.
 * @param {*} meta - Metadata to pass to the close event.
 */
export function closeModal(id, meta) {
    Modal.close(id, meta);
}

/**
 * Close all open modals in reverse order (topmost first)
 */
export function closeAllModals() {
    Modal.closeAll();
}

/**
 * Retrieve data passed to an open modal
 * @param {String} id - The ID of the modal.
 * @returns {*} The data object or null.
 */
export function getModalData(id) {
    return Modal.getData(id);
}

export function consoleLogOnLocal(...args) {
    if (appIsLocal()) {
        console.log(...args);
    }
}

export function consoleErrorOnLocal(...args) {
    if (appIsLocal()) {
        console.error(...args);
    }
}

/**
 *
 * @param {import("modular-openscriptjs").State<T>} state
 * @param {(currentValue: T) => T} callback
 */
export function updateStateValue(state, callback) {
    let currentValue = JSON.parse(JSON.stringify(state.value));
    let newValue = callback(currentValue);
    state.value = newValue;
}

/**
 * Generates a beautiful random light pastel linear gradient CSS value
 * using harmonious HSL color spaces.
 * @returns {String} CSS linear-gradient value
 */
export function randomLightGradient() {
    const hue1 = Math.floor(Math.random() * 360);
    const hue2 = (hue1 + randomInt(40, 100)) % 360;
    const saturation1 = randomInt(65, 85);
    const saturation2 = randomInt(65, 85);
    const lightness1 = randomInt(55, 68);
    const lightness2 = randomInt(55, 68);
    return `linear-gradient(135deg, hsl(${hue1}, ${saturation1}%, ${lightness1}%), hsl(${hue2}, ${saturation2}%, ${lightness2}%))`;
}

/**
 * Computes commission rates and payers dynamically based on the price.
 * @param {number} price
 * @returns {object} { rate, payer, customerRate, businessRate }
 */
export function getCommissionRatesForPrice(price) {
    const systemConfigs = window.systemConfigs || {};

    const rateUpTo50 = systemConfigs.commissionRateUpTo50 ?? 5;
    const rateUpTo100 = systemConfigs.commissionRateUpTo100 ?? 10;
    const rateUpTo150 = systemConfigs.commissionRateUpTo150 ?? 12;
    const rateAbove150 = systemConfigs.commissionRateAbove150 ?? 15;

    const payerUpTo50 = systemConfigs.commissionPayerUpTo50 ?? "customer";
    const payerUpTo100 = systemConfigs.commissionPayerUpTo100 ?? "customer";
    const payerUpTo150 = systemConfigs.commissionPayerUpTo150 ?? "split";
    const payerAbove150 = systemConfigs.commissionPayerAbove150 ?? "split";

    let rate = 15;
    let payer = "split";

    if (price <= 50) {
        rate = rateUpTo50;
        payer = payerUpTo50;
    } else if (price <= 100) {
        rate = rateUpTo100;
        payer = payerUpTo100;
    } else if (price <= 150) {
        rate = rateUpTo150;
        payer = payerUpTo150;
    } else {
        rate = rateAbove150;
        payer = payerAbove150;
    }

    let company = window.currentCompany;

    let customerRate = 0;
    let businessRate = 0;

    if (payer === "customer") {
        customerRate = rate;
        businessRate = 0;
    } else if (payer === "business") {
        customerRate = 0;
        businessRate = rate;
    } else if (payer === "split") {
        let defaultBusinessRate = company.commission_rate;
        let defaultCustomerRate = company.customer_commission_rate;

        let total = defaultBusinessRate + defaultCustomerRate;

        let cPercent = defaultCustomerRate / total;
        let bPercent = defaultBusinessRate / total;

        customerRate = Math.round(cPercent * rate);
        businessRate = Math.round(bPercent * rate);
    }

    return {
        rate,
        payer,
        customerRate,
        businessRate,
    };
}

export async function dataURLtoBlob(dataurl) {
    const res = await fetch(dataurl);
    const blob = await res.blob();
    const arrayBuffer = await blob.arrayBuffer();
    return new Blob([arrayBuffer], { type: blob.type });
}

export function appendToFormData(formData, data, parentKey = "") {
    if (data === null || data === undefined) return;

    if (data instanceof Blob) {
        formData.append(parentKey, data);
    } else if (data instanceof File) {
        formData.append(parentKey, data);
    } else if (typeof data === "boolean") {
        formData.append(parentKey, data ? 1 : 0);
    } else if (Array.isArray(data)) {
        data.forEach((val, index) => {
            appendToFormData(formData, val, `${parentKey}[${index}]`);
        });
    } else if (typeof data === "object" && !(data instanceof Date)) {
        Object.keys(data).forEach((key) => {
            const fullKey = parentKey ? `${parentKey}[${key}]` : key;
            appendToFormData(formData, data[key], fullKey);
        });
    } else {
        formData.append(parentKey, data);
    }
}

export function setActiveNav(key) {
    setTimeout(() => {
        dom.all(`[app-hook="app-nav"]`).forEach((el) => {
            if (el.getAttribute("app-nav-hook") === key) {
                el.classList.add("active-nav");
            } else {
                el.classList.remove("active-nav");
            }
        });
    }, 1000);
}

export function unfreezeBodyWhenNoModalOrOffcanvas() {
    const body = dom.get("body");

    if (body) {
        const styles = getComputedStyle(body);

        const hasModalBodyStyles =
            styles.overflow === "hidden" && styles.paddingRight === "0px";

        const hasOpenBootstrapComponent =
            dom.get(".modal.show")?.length > 0 ||
            dom.get(".offcanvas.show")?.length > 0;

        const hasWabModalOpen = dom.get(".wab-modal-open")?.length > 0;

        if (
            hasModalBodyStyles &&
            !hasOpenBootstrapComponent &&
            !hasWabModalOpen
        ) {
            body.style.removeProperty("overflow");
            body.style.removeProperty("padding-right");
        }
    }
}
